import React, { useMemo, useState } from 'react';
import { LayoutGridIcon, ListIcon, PlusIcon, WrenchIcon } from 'lucide-react';
import { Employee, JobStatus, ProJob } from '../../types/pro';
import { JOB_STATUS, formatDate, formatTime, hoursOnJob } from '../../utils/pro';
import { TimeEntry } from '../../types/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { SearchField } from '../../components/pro/SearchField';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProJobsProps {
  jobs: ProJob[];
  employees: Employee[];
  timeEntries: TimeEntry[];
  onOpen: (jobId: string) => void;
  onSetStatus: (job: ProJob, status: JobStatus) => void;
  /** Opens the create-job dialog — no quote required. */
  onNew: () => void;
}
const ORDER: JobStatus[] = ['ikke-planlagt', 'planlagt', 'i-gang', 'afventer', 'faerdig', 'faktureret'];
export function ProJobs({
  jobs,
  employees,
  timeEntries,
  onOpen,
  onSetStatus,
  onNew
}: ProJobsProps) {
  const [view, setView] = useState<'liste' | 'braet'>(() => {
    if (typeof window === 'undefined') return 'liste';
    return localStorage.getItem('proJobsView') as 'liste' | 'braet' || 'liste';
  });
  const [query, setQuery] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<JobStatus | null>(null);
  const setViewMode = (next: 'liste' | 'braet') => {
    setView(next);
    localStorage.setItem('proJobsView', next);
  };
  const employeeById = useMemo(() => new Map(employees.map(e => [e.id ?? '', e])), [employees]);
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return jobs;
    return jobs.filter(job => [job.title, job.jobNo, job.customerName, job.address, job.city].join(' ').toLowerCase().includes(term));
  }, [jobs, query]);
  const active = filtered.filter(job => !['faerdig', 'faktureret'].includes(job.status)).length;
  const renderMeta = (job: ProJob) => {
    const registered = hoursOnJob(timeEntries, job.id ?? '');
    const over = registered > job.estimatedHours && job.estimatedHours > 0;
    return <span className={`whitespace-nowrap text-xs tabular-nums ${over ? 'font-semibold text-amber-600' : 'text-stone-500'}`} data-id="element-1725" data-mp="34wz3lmonma1r">
        {registered.toLocaleString('da-DK')} / {job.estimatedHours} t
      </span>;
  };
  return <div data-id="element-1726" data-mp="6ds8bxqk9rn9">
      <PageHeader title="Opgaver" subtitle={`${active} aktive af ${jobs.length}`} actions={<>
            <div role="group" aria-label="Visning" className="flex rounded-lg border border-stone-200 bg-white p-1" data-id="element-1728" data-mp="3nrw9r3z621ca">
              {[{
          id: 'liste' as const,
          label: 'Liste',
          icon: ListIcon
        }, {
          id: 'braet' as const,
          label: 'Bræt',
          icon: LayoutGridIcon
        }].map(option => {
          const Icon = option.icon;
          return <button key={option.id} onClick={() => setViewMode(option.id)} aria-pressed={view === option.id} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${view === option.id ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1729" data-mp="24w41pwe7o1h3">
                    <Icon className="h-3.5 w-3.5" data-id="element-1730" data-mp="1zwhmjhetob5n" />
                    {option.label}
                  </button>;
        })}
            </div>
            <button onClick={onNew} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1731" data-mp="3bbef31a7tihr">
              <PlusIcon className="h-4 w-4" data-id="element-1732" data-mp="5hm10j5ep4a5" />
              Ny opgave
            </button>
          </>} data-id="element-1727" data-mp="6ch3duldqduk" />

      <SearchField value={query} onChange={setQuery} placeholder="Søg opgave, kunde eller adresse…" className="mb-4 max-w-md" data-id="element-1733" data-mp="ah8durhboiif" />

      {filtered.length === 0 ? <EmptyState icon={WrenchIcon} title={jobs.length === 0 ? 'Ingen opgaver endnu' : 'Ingen opgaver matcher søgningen'} description={jobs.length === 0 ? 'Opret en opgave direkte — du behøver ikke lave et tilbud først.' : undefined} data-id="element-1734" data-mp="kf4kb5zbepa7" /> : view === 'liste' ? <div className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-1735" data-mp="134b6x438c11a">
          <ul className="divide-y divide-stone-100" data-id="element-1736" data-mp="1858mra0roizm">
            {[...filtered].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || (a.start ?? '').localeCompare(b.start ?? '')).map(job => {
          const employee = employeeById.get(job.employeeIds[0] ?? '');
          return <li key={job.id} data-id="element-1737" data-mp="2nnewfwjruekl">
                    <button onClick={() => job.id && onOpen(job.id)} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1738" data-mp="2insc735izy2a">
                      <span className="w-24 flex-shrink-0 font-mono text-xs text-stone-400" data-id="element-1739" data-mp="3bcziwpf4vtym">
                        {job.jobNo}
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-1740" data-mp="31n6afxaafuyl">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1741" data-mp="31kk2vxnedfgd">
                          {job.title}
                        </span>
                        <span className="block truncate text-xs text-stone-500" data-id="element-1742" data-mp="3uaafu3dambaw">
                          {job.customerName} · {job.address}, {job.city}
                        </span>
                      </span>
                      <span className="hidden w-28 flex-shrink-0 text-xs text-stone-500 sm:block" data-id="element-1743" data-mp="1wevagy6tgb1n">
                        {job.start ? `${formatDate(job.start)} ${formatTime(job.start)}` : 'Ikke planlagt'}
                      </span>
                      {employee && <span className="hidden items-center gap-1.5 text-xs font-medium text-stone-600 md:flex" data-id="element-1744" data-mp="18rwu00ip533q">
                          <span className="h-2 w-2 rounded-full" style={{
                  backgroundColor: employee.color
                }} data-id="element-1745" data-mp="zp45u64t97i4" />
                          {employee.name.split(' ')[0]}
                        </span>}
                      {renderMeta(job)}
                      <StatusChip meta={JOB_STATUS[job.status]} data-id="element-1746" data-mp="2z16x42jof66c" />
                    </button>
                  </li>;
        })}
          </ul>
        </div> : <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6" data-id="element-1747" data-mp="347v8l2fbr8p2">
          <div className="flex min-w-max gap-3" data-id="element-1748" data-mp="3hy8fr2ke6jdc">
            {ORDER.map(status => {
          const items = filtered.filter(job => job.status === status);
          const isTarget = dragOver === status;
          return <section key={status} onDragOver={e => {
            e.preventDefault();
            setDragOver(status);
          }} onDragLeave={() => setDragOver(null)} onDrop={() => {
            const job = filtered.find(j => j.id === dragId);
            if (job && job.status !== status) onSetStatus(job, status);
            setDragId(null);
            setDragOver(null);
          }} className={`flex w-[260px] flex-shrink-0 flex-col rounded-xl border transition-colors ${isTarget ? 'border-brand-400 bg-brand-50/60' : 'border-stone-200 bg-stone-50'}`} data-id="element-1749" data-mp="3rvd3v7xssows">
                  <header className="flex items-center gap-2 border-b border-stone-200/70 px-3 py-2.5" data-id="element-1750" data-mp="30t9je1iasyg7">
                    <span className={`h-2 w-2 rounded-full ${JOB_STATUS[status].dot}`} data-id="element-1751" data-mp="34ta0jtws7nqy" />
                    <h2 className="flex-1 truncate text-sm font-semibold text-stone-800" data-id="element-1752" data-mp="2d2lxudfgmab6">
                      {JOB_STATUS[status].label}
                    </h2>
                    <span className="rounded bg-white px-1.5 py-0.5 text-xs font-semibold tabular-nums text-stone-600" data-id="element-1753" data-mp="2h35vdi3y3fxu">
                      {items.length}
                    </span>
                  </header>

                  <ul className="flex-1 space-y-2 p-2" data-id="element-1754" data-mp="3slqfhvl82zm1">
                    {items.map(job => <li key={job.id} data-id="element-1755" data-mp="1ffhy9ff8gzg">
                        <div draggable onDragStart={() => setDragId(job.id ?? null)} onDragEnd={() => {
                  setDragId(null);
                  setDragOver(null);
                }} className={`rounded-lg border border-stone-200 bg-white p-3 transition-shadow hover:shadow-sm ${dragId === job.id ? 'opacity-50' : ''}`} data-id="element-1756" data-mp="bhj8u5oq6ngp">
                          <button onClick={() => job.id && onOpen(job.id)} className="w-full text-left" data-id="element-1757" data-mp="3jvrx51k7ss32">
                            <p className="font-mono text-[10px] text-stone-400" data-id="element-1758" data-mp="3axzhfwakzif5">
                              {job.jobNo}
                            </p>
                            <p className="mt-0.5 truncate text-sm font-semibold text-stone-900" data-id="element-1759" data-mp="194iayvy6snlz">
                              {job.title}
                            </p>
                            <p className="mt-0.5 truncate text-xs text-stone-500" data-id="element-1760" data-mp="2rnrbdxkslxt3">
                              {job.customerName}
                            </p>
                            <p className="mt-2 flex items-center justify-between gap-2 border-t border-stone-100 pt-2 text-xs text-stone-400" data-id="element-1761" data-mp="31jkk15ve575l">
                              <span data-id="element-1762" data-mp="28vezok891k6a">
                                {job.start ? formatDate(job.start) : '—'}
                              </span>
                              {renderMeta(job)}
                            </p>
                          </button>

                          <label className="mt-2 block lg:hidden" data-id="element-1763" data-mp="2imjcbdagcsxy">
                            <span className="sr-only" data-id="element-1764" data-mp="2du8fijb0dn9m">Skift status</span>
                            <select value={job.status} onChange={e => onSetStatus(job, e.target.value as JobStatus)} className="w-full rounded-md border border-stone-200 bg-stone-50 px-2 py-1.5 text-xs font-medium text-stone-700 outline-none focus:ring-2 focus:ring-brand-500" data-id="element-1765" data-mp="1q4ns9o7to3uj">
                              {ORDER.map(option => <option key={option} value={option} data-id="element-1766" data-mp="3rtbkkz92ztdw">
                                  {JOB_STATUS[option].label}
                                </option>)}
                            </select>
                          </label>
                        </div>
                      </li>)}
                    {items.length === 0 && <li className="rounded-lg border border-dashed border-stone-200 px-3 py-6 text-center text-xs text-stone-400" data-id="element-1767" data-mp="fbwub7xiijqv">
                        Tom
                      </li>}
                  </ul>
                </section>;
        })}
          </div>
        </div>}
    </div>;
}